import {
  Banner,
  Box,
  Divider,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { SubflowExits, SubflowMessengerAction } from 'lib-shared-types-api';
import { useCallback, useContext, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMount } from 'react-use';
import { v4 as uuid } from 'uuid';

import { FlowContext } from 'app/views/layout/views/settings/views/messenger/views/editFlow/flowEditorRoot';
import { HookFormSelectionListSingle, HookFormText } from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { FlowMessengerNodeSubflowReference } from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { Relation } from 'fable/flowEditor/components/flowManager/canvas/types/relation';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { DixaMessengerSubflow } from '../../../hooks';

type SubflowReferenceFormValues = {
  subflowId?: string;
  subflowName?: string;
};

const subflowRefFormFieldNames = {
  name: 'subflowName',
  id: 'subflowId',
} as const;

type Props =
  FlowManagerEditModalComponentT<FlowMessengerNodeSubflowReference> & {
    highlightedNodes: Relation[];
    allowUsageOfExistingSubflow: boolean;
    onDeleteSelection?: (nodeIds: string[]) => void;
  };

export function SubflowReferenceModal(props: Props): JSX.Element | null {
  const {
    triggerClose,
    replaceNode,
    node,
    highlightedNodes,
    allowUsageOfExistingSubflow,
    onDeleteSelection,
  } = props;
  const notify = useNotifications();
  const navigate = useAppNavigate();

  const shouldBeRedirectedToSubflowEditor = node?.payload?.subflowId
    ? true
    : false;

  const {
    subflows,
    flowId,
    setSubflows,
    initialFlow,
    setNavigateAwayPromptEnabled,
    setHighlightedNodes,
  } = useContext(FlowContext);

  const anySubflowsAlreadyDefined = subflows.length > 0;

  const { formState, control, handleSubmit } =
    useForm<SubflowReferenceFormValues>({ mode: 'all' });

  const nameFieldValue = useWatch({
    control,
    name: subflowRefFormFieldNames.name,
  });

  const idFieldValue = useWatch({
    control,
    name: subflowRefFormFieldNames.id,
  });

  const redirectToSubflowEditor = useCallback(
    (id: string): void => {
      if (!flowId) return;

      setNavigateAwayPromptEnabled(false);

      // Use setTimeout to ensure that the navigation happens after the context state is updated
      setTimeout(() => {
        navigate({
          path: '/settings/dixa-messenger/flow/:flowId/sub-flow/edit/:subflowId',
          params: { path: { flowId, subflowId: id } },
        });

        setNavigateAwayPromptEnabled(true);
      }, 100);
    },
    [flowId, navigate, setNavigateAwayPromptEnabled],
  );

  useMount(() => {
    if (highlightedNodes?.length > 0) {
      const hasSubflowReference = highlightedNodes.some(
        (node) => node.nodeType === (FlowNodeTypes.SUBFLOW_REFERENCE as string),
      );
      if (!hasSubflowReference) {
        setHighlightedNodes(highlightedNodes as SubflowMessengerAction[]);
      }
    } else {
      setHighlightedNodes([]);
    }
  });

  useEffect(() => {
    // If the node has a subflowId in payload, we have to redirect to the subflow editor
    if (shouldBeRedirectedToSubflowEditor) {
      const subflowId = node.payload?.subflowId;

      if (subflowId) {
        redirectToSubflowEditor(subflowId);
      }
    }
  }, [redirectToSubflowEditor, shouldBeRedirectedToSubflowEditor, node]);

  const createNewSubflow = useCallback(
    (name: string): DixaMessengerSubflow => {
      return {
        id: uuid(),
        name,
        actions: [
          {
            nodeType: FlowNodeTypes.INIT,
            childIds: [],
            id: uuid(),
            payload: null,
            isRemovable: false,
            isEditable: false,
            pageX: 0,
            pageY: 0,
            parentId: null,
            connectionLabel: '',
            //@ts-expect-error prototyping
            isAction: false, // all flow nodes should have isAction set to false except for the temporary plus buttton which is used for adding new nodes
            icon: null,
            badges: [],
            overlay: [],
            text: '',
          },
        ],
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        channel: 'messenger',
        org_id: initialFlow?.org_id,
      };
    },
    [initialFlow?.org_id],
  );

  const createSubflowReferenceNode = useCallback(
    (id: string): FlowMessengerNodeSubflowReference => {
      const theSubflow = subflows.find(({ id }) => id === id);

      const subflowReturns =
        theSubflow?.actions.filter(
          (action) => action.nodeType === FlowNodeTypes.SUBFLOW_RETURN,
        ) ?? [];

      const referenceNodeExits: SubflowExits = subflowReturns.reduce(
        (acc, { id }) => {
          acc[id] = { nextNodeId: uuid() };
          return acc;
        },
        {} as SubflowExits,
      );

      return {
        ...node,
        text: '',
        isEditable: true,
        isRemovable: true,
        isAction: false,
        nodeType: FlowNodeTypes.SUBFLOW_REFERENCE,
        payload: {
          subflowId: id,
          exits: referenceNodeExits,
        },
        overlay: [],
        badges: [],
      };
    },
    [node, subflows],
  );

  const handleSaveClick = useCallback(
    (values: SubflowReferenceFormValues) => {
      if (!values.subflowId && !values.subflowName) {
        const notificationText =
          'Either enter a name for the new sub-flow or select an existing one';
        return notify({ title: notificationText }, NotificationType.Error);
      }

      const { subflowId, subflowName } = values;
      const shouldCreateNewSubflow = Boolean(subflowName);

      // Refactor this to use the flow context to add a new subflow with a given name and a unique id
      if (shouldCreateNewSubflow) {
        const newSubflow = createNewSubflow(subflowName!);

        setSubflows([...subflows, newSubflow]);

        const node = createSubflowReferenceNode(newSubflow.id);
        /**
         * TODO: this implementation does not support draft state for the new
         * subflow. Draft state is planned for a feature release.
         * github.com/dixahq/agent-interface/pull/7743#discussion_r1608352791
         */
        replaceNode(node);

        if (highlightedNodes?.length > 0) {
          /**
           * Remove highlighted nodes from the main flow if the user have chosen
           * to create a sub flow based on highlighted nodes.
           */
          const nodesEligibleForDeletion = highlightedNodes
            .slice(1)
            .map((node) => node.id);
          if (nodesEligibleForDeletion.length > 0) {
            onDeleteSelection?.(nodesEligibleForDeletion);
          }
          ampli.subflowCreatedFromHighlightedNodes();
        }

        ampli.subflowCreatedFromScratch();

        redirectToSubflowEditor(newSubflow.id);
        triggerClose();
      } else {
        const node = createSubflowReferenceNode(subflowId!);
        ampli.subflowReused();
        replaceNode(node);
        triggerClose();
      }
    },
    [
      notify,
      createNewSubflow,
      setSubflows,
      subflows,
      createSubflowReferenceNode,
      replaceNode,
      highlightedNodes,
      redirectToSubflowEditor,
      triggerClose,
      onDeleteSelection,
    ],
  );

  const doesSubflowNameExist = (name: string): boolean => {
    return subflows.some(
      ({ name: subflowName }) =>
        subflowName.toLocaleLowerCase() === name.toLocaleLowerCase(),
    );
  };

  if (shouldBeRedirectedToSubflowEditor) {
    // show a spinner while waiting to be redirected to the subflow editor
    return (
      <FlexBox justifyContent="center" alignItems="center" padding="300">
        <Spinner />
      </FlexBox>
    );
  }

  const formHasErrors = Object.keys(formState.errors).length > 0;
  const disabledSubmit = !formState.isDirty || formHasErrors;
  const disabledSelectInput = Boolean(nameFieldValue);
  const disabledNameInput = Boolean(idFieldValue);
  const shouldCreateNewSubflow = Boolean(nameFieldValue);
  const createSubflowFromHighlightedNode =
    allowUsageOfExistingSubflow === false && highlightedNodes.length > 0;

  return (
    <Box width="600px">
      <form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalHead title="Add sub-flow" onClose={triggerClose} />

        <ModalBody>
          <FlexBox flexDirection="column" gap="300">
            <Banner
              icon="info"
              variant="info"
              description={
                <Text.Paragraph variant="4">
                  “Sub-flows” are reusable branches that you can insert into
                  various parts of the main flow for repeated use.
                </Text.Paragraph>
              }
            />

            <Text.Paragraph
              variant="2"
              color={disabledNameInput ? 'neutral.500' : undefined}
            >
              {createSubflowFromHighlightedNode
                ? 'Create a new sub-flow from the highlighted nodes.'
                : 'Create a new sub-flow from scratch.'}
            </Text.Paragraph>

            <HookFormText
              label="Feedback after form submission"
              name={subflowRefFormFieldNames.name}
              control={control}
              disabled={formState.isSubmitting}
              componentProps={{
                id: subflowRefFormFieldNames.name,
                placeholder: 'Give a name to the sub-flow',
                maxLength: 300,
              }}
              rules={{
                required: disabledNameInput
                  ? false
                  : 'Please enter a name for the sub-flow',
                validate: (value = '') =>
                  doesSubflowNameExist(value)
                    ? 'A sub-flow with this name already exists'
                    : true,
              }}
            />

            {allowUsageOfExistingSubflow && anySubflowsAlreadyDefined && (
              <>
                <Divider orientation="horizontal">Or</Divider>

                <Text.Paragraph
                  variant="2"
                  color={disabledSelectInput ? 'neutral.500' : undefined}
                >
                  Select an existing sub-flow from the list below.
                </Text.Paragraph>

                <HookFormSelectionListSingle
                  name={subflowRefFormFieldNames.id}
                  label="Existing sub-flow"
                  disabled={disabledSelectInput || formState.isSubmitting}
                  control={control}
                  rules={{
                    required: disabledSelectInput
                      ? false
                      : 'Please select a sub-flow',
                  }}
                  componentProps={{
                    id: subflowRefFormFieldNames.id,
                    getItemTitleByValue: (value) =>
                      subflows.find(({ id }) => id === value)?.name,
                  }}
                >
                  {subflows.map(({ id, name }) => {
                    return (
                      <SelectionList.Item key={id} value={id}>
                        {name}
                      </SelectionList.Item>
                    );
                  })}
                </HookFormSelectionListSingle>
              </>
            )}
          </FlexBox>
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName={
            shouldCreateNewSubflow ? 'Create new sub-flow' : 'Add sub-flow'
          }
          confirmProps={{ disabled: disabledSubmit }}
          cancelOnClick={triggerClose}
        />
      </form>
    </Box>
  );
}
